import { Helmet } from "react-helmet";
import {
  useLocation,
  useParams,
  useBlocker,
  useNavigate,
} from "react-router-dom";
import { useEffect, useState, useRef, Dispatch, SetStateAction } from "react";
import { useSearchStore } from "store/searchStore";
import { useQuery } from "@tanstack/react-query";
import { getExtendedProfileData, getProfileV2 } from "data/fetch";

import { BusinessItem } from "types/results-panel.types";

// components
import EditSectionNavigation from "components/EditSectionNavigation/EditSectionNavigation";
import ParticipationSection from "components/EditBusinessProfileSections/ParticipationSection/ParticipationSection";
import OverviewSection from "components/EditBusinessProfileSections/OverviewSection/OverviewSection";
import OrganizationSection from "components/EditBusinessProfileSections/OrganizationSection/OrganizationSection";
import CertificationsSection from "components/EditBusinessProfileSections/CertificationsSection/CertificationsSection";
import NAICSSection from "components/EditBusinessProfileSections/NAICSSection/NAICSSection";
import ServiceSection from "components/EditBusinessProfileSections/ServiceSection/ServiceSection";
import ExportSection from "components/EditBusinessProfileSections/ExportSection/ExportSection";
import PerformanceSection from "components/EditBusinessProfileSections/PerformanceSection/PerformanceSection";
import SizeProtestSection from "components/EditBusinessProfileSections/SizeProtestSection/SizeProtestSection";
import { Alert } from "@trussworks/react-uswds";

import ProfileEditDialog from "components/Dialogs/EditProfileDialog/ProfileEditDialog";

import { Loader } from "components/icons";
import NotListedPage from "pages/NotListedPage/NotListedPage";

// store
import { useResponsiveStore } from "store/useResponsiveStore";
import {
  EditedProfileData,
  useProfileEditStore,
} from "store/useProfileEditStore";
import useAuthStore from "store/useAuthStore";

// styles
import styles from "./BusinessProfileEdit.module.scss";

// types
import { NaicsProfile } from "types/profile.types";

// query
import { useProfileQuery } from "../BusinessProfileDetails/query";

// constants
import { editSideNavItems } from "constants/side-nav";

// mock data
import { MOCK_EXTENDED_PROFILE_DATA } from "data/extended-profile-mock-data";

const sections = (
  setUnsavedChanges: Dispatch<SetStateAction<boolean>>,
  naics?: NaicsProfile[],
  businessProfileData?: BusinessItem
) => ({
  "#participation": (
    <ParticipationSection
      setUnsavedChanges={setUnsavedChanges}
      businessProfileData={businessProfileData}
    />
  ),
  "#size-determination": (
    <SizeProtestSection 
    setUnsavedChanges={setUnsavedChanges} 
    businessProfileData={businessProfileData}
    />
  ),
  "#overview-information": (
    <OverviewSection
      setUnsavedChanges={setUnsavedChanges}
      businessProfileData={businessProfileData}
    />
  ),
  "#organization-ownership": (
    <OrganizationSection
      setUnsavedChanges={setUnsavedChanges}
      businessProfileData={businessProfileData}
    />
  ),
  "#certifications": (
    <CertificationsSection
      setUnsavedChanges={setUnsavedChanges}
      businessProfileData={businessProfileData}
    />
  ),
  "#naics-codes": <NAICSSection naics={naics} businessProfileData={businessProfileData} />,
  "#service-information": (
    <ServiceSection setUnsavedChanges={setUnsavedChanges} />
  ),
  "#export-information": (
    <ExportSection setUnsavedChanges={setUnsavedChanges} />
  ),
  "#performance-history": (
    <PerformanceSection setUnsavedChanges={setUnsavedChanges} />
  ),
});

export type SectionsProps = {
  setUnsavedChanges: Dispatch<React.SetStateAction<boolean>>;
  businessProfileData?: BusinessItem;
};

export default function BusinessProfileEdit() {
  const { setMenuComponent } = useResponsiveStore();
  const { hash } = useLocation();
  const activeSection = hash ? hash : "#participation";

  const { uei, cage_code } = useParams();

  const { setExtendedProfile } = useProfileEditStore((state) => ({
    setExtendedProfile: state.setExtendedProfile,
  }));

  const { user, newClaimedBusinesses, updateBusinessVerifiedStatus } = useAuthStore();

  // fetch profile data
  const {
    data: profileData,
    isPending: profilePending,
    errorType: profileErrorType,
  } = useProfileQuery(uei, cage_code);

  // fetch extended profile data
  const {
    data: editProfileData,
    isPending: editProfilePending,
    isError: editProfileError,
    error: editProfileErrorObj,
  } = useQuery<EditedProfileData>({
    queryKey: ["extended-profile", uei],

    queryFn: () => getExtendedProfileData(uei),

    gcTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    enabled: Boolean(uei),
    retry: false,
    throwOnError: () => false,

    // TODO: mock initial data
    // initialData: MOCK_EXTENDED_PROFILE_DATA,
    // initialDataUpdatedAt: Date.now(),
  });

  const [businessProfileData, setBusinessProfileData] =
    useState<BusinessItem | null>(null);
  const [warningDialog, setWarningDialog] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [errorType, setErrorType] = useState<
    "UN_AUTHORIZED" | "NOT_FOUND" | "SERVER_ERROR" | null
  >(null);

  // Check if the business is newly claimed by checking "isNew" in newClaimedBusinesses
  const showVerified = newClaimedBusinesses?.find(biz => biz.uei === uei)?.isNew || false;
  const userRole = user?.role || "user"

  const isLoading = profilePending || editProfilePending;

  const isVisible = editSideNavItems
    .find((nav) => nav.path === activeSection)
    ?.roles.includes(userRole);

  const currentSection = isVisible
    ? sections(setUnsavedChanges, profileData?.naics, businessProfileData)[
        activeSection
      ]
    : null;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation, historyAction }) => {
      // only works for navigating within React Router, not document requests
      const navigating = nextLocation.pathname !== currentLocation.pathname;
      const hashChange = nextLocation.hash !== currentLocation.hash;

      if (navigating || hashChange) {
        if (showVerified) {
          updateBusinessVerifiedStatus(uei)
        }
      }

      if (
        (navigating && unsavedChanges) ||
        (hashChange && unsavedChanges && historyAction === "REPLACE") // don't show dialog on "POP" or "PUSH" as we can't prevent it
      ) {
        setWarningDialog(true);
        return true;
      }
      return false;
    }
  );

  useEffect(() => {
    if (editProfileData) {
      setExtendedProfile(editProfileData.extendedProfile);
    }
  }, [editProfileData]);

  useEffect(() => {
    if (profileData?.entity) {
      setBusinessProfileData(profileData.entity);
    }
  }, [profileData]);

  useEffect(() => {
    if (editProfileError && editProfileErrorObj) {
      const status = (editProfileErrorObj as any).status || 500;
      switch (status) {
        case 401:
        case 403: // match it with `editProfileMiddleware`
          setErrorType("UN_AUTHORIZED");
          break;
        case 404:
          setErrorType("NOT_FOUND");
          break;
        case 500:
          setErrorType("SERVER_ERROR");
          break;
        default:
          setErrorType("SERVER_ERROR");
          break;
      }
    } else {
      setErrorType(null); // Clear error state when no errors
    }
  }, [editProfileError, editProfileErrorObj]);

  useEffect(() => {
    if (profileErrorType) {
      setErrorType(profileErrorType);
      setBusinessProfileData(null);
    }
  }, [profileErrorType]);

  useEffect(() => {
    setMenuComponent(<EditSectionNavigation />);
    return () => {
      setMenuComponent(null);
    };
  }, []);

  useEffect(() => {
    if (warningDialog === false && blocker.state === "blocked") {
      blocker.reset();
    }
  }, [warningDialog]);

  useEffect(() => {
    // handle document requests
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (showVerified) {
        updateBusinessVerifiedStatus(uei);
      }

      if (unsavedChanges) {
        e.preventDefault();
        setWarningDialog(false);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [unsavedChanges]);

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Loading...</title>
        </Helmet>
        <div className={styles["loader"]}>
          <Loader />
        </div>
      </>
    );
  }

  if (errorType === "NOT_FOUND") {
    return (
      <>
        <Helmet>
          <title>
            {businessProfileData?.legal_business_name} - Not Listed | SBA
            Small Business Search
          </title>
        </Helmet>
        <NotListedPage
          businessName={businessProfileData?.legal_business_name}
          />
      </>
    );
  }

  if (errorType === "UN_AUTHORIZED") {
    return (
      <>
        <Helmet>
          <title>Unauthorized</title>
        </Helmet>
        <div className={styles["error-container"]}>
          <Alert
            type="error"
            heading="You do not have permission to view this page"
            headingLevel="h4"
          >
            If you believe this is an error, refresh the page, ensure that you
            are signed in and try again. If you are still unable,{" "}
            <a
              href="https://sbaone.atlassian.net/servicedesk/customer/portal/1016/group/1045/create/11743"
              target="_blank"
            >
              reach out to our support team
            </a>
            .
          </Alert>
        </div>
      </>
    );
  }

  if (errorType === "SERVER_ERROR") {
    return (
      <>
        <Helmet>
          <title>Server Error</title>
        </Helmet>
        <div className={styles["error-container"]}>
          <Alert type="error" heading="Server Error" headingLevel="h4">
            Unable to fetch profile data. Please try again later.
          </Alert>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {businessProfileData?.legal_business_name ?? "Loading"} - Edit | SBA
          Small Business Search
        </title>
      </Helmet>
      <div className={styles["business-profile-edit-root"]}>
        {/* <div>banner section</div> */}

        {showVerified && (
          <Alert
            type={"success"}
            headingLevel={"h4"}
            className={styles["alert-wrapper"]}
          >
            <p className={styles["alert-text"]}>
              {businessProfileData?.legal_business_name} successfully verified
            </p>
          </Alert>
        )}

        <div className={styles["header"]}>
          <h3 className={styles["heading"]}>
            Edit {businessProfileData?.legal_business_name}
          </h3>
          <p className={styles["desc"]}>
            Review and modify the information that appears in your small
            business’s profile to show up in the right search results.
          </p>
        </div>

        <div className={styles["main-content"]}>
          <div className={styles["side-nav"]}>
            <EditSectionNavigation />
          </div>

          <div className={styles["content"]}>{currentSection}</div>
        </div>
      </div>

      <ProfileEditDialog
        open={warningDialog}
        setOpen={setWarningDialog}
        blocker={blocker}
      />
    </>
  );
}
